<template>
  <v-card
    class="modern-card"
    variant="flat"
  >
    <!-- Header com status da conexão -->
    <v-card-text
      v-if="!isAutoLogin"
      class="pa-0"
    >
      <div
        class="connection-status"
        :class="{ connected: socketConnected, disconnected: !socketConnected }"
      >
        <span class="material-icons icon-sm">
          {{ socketConnected ? 'wifi' : 'wifi_off' }}
        </span>
        {{ socketConnected ? 'Conectado' : 'Desconectado' }}
        <v-progress-circular
          v-if="socketLoading"
          indeterminate
          size="16"
          width="2"
          class="ml-2"
        />
      </div>
    </v-card-text>

    <!-- Tabs modernas com largura aumentada -->
    <v-tabs 
      v-model="tab"
      class="modern-tabs wide-tabs"
      color="primary"
      align-tabs="center"
      grow
    >
      <v-tab 
        value="disponiveis"
        class="modern-tab wide-tab"
        :class="{ 'has-priority': qtdChamadasPrioridade > 0 }"
      >
        <div class="tab-content wide-tab-content">
          <div class="icon-badge-container">
            <span class="material-icons tab-icon">groups</span>
            <v-badge
              :content="listaDisponiveisExibicao.length"
              :color="getBadgeColor('disponiveis')"
              class="icon-badge"
              floating
            />
          </div>
          <div class="tab-text-content">
            <span class="tab-label">Disponíveis</span>
            <div
              v-if="qtdChamadasPrioridade > 0"
              class="priority-indicator"
            >
              {{ qtdChamadasPrioridade }} prioritário(s)
            </div>
          </div>
        </div>
      </v-tab>

      <v-tab
        value="espera"
        class="modern-tab wide-tab"
      >
        <div class="tab-content wide-tab-content">
          <div class="icon-badge-container">
            <span class="material-icons tab-icon">pause_circle</span>
            <v-badge
              :content="listaEspera.length"
              :color="getBadgeColor('espera')"
              class="icon-badge"
              floating
            />
          </div>
          <div class="tab-text-content">
            <span class="tab-label">Em espera</span>
          </div>
        </div>
      </v-tab>

      <v-tab
        value="atendidos"
        class="modern-tab wide-tab"
      >
        <div class="tab-content wide-tab-content">
          <div class="icon-badge-container">
            <span class="material-icons tab-icon">check_circle</span>
            <v-badge
              :content="listaAtendidos.length"
              :color="getBadgeColor('atendidos')"
              class="icon-badge"
              floating
            />
          </div>
          <div class="tab-text-content">
            <span class="tab-label">Atendidos</span>
          </div>
        </div>
      </v-tab>
    </v-tabs>

    <v-divider />

    <!-- Conteúdo das tabs com altura aumentada -->
    <v-window
      v-model="tab"
      class="modern-window"
    >
      <!-- Tab Disponíveis -->
      <v-window-item value="disponiveis">
        <v-row class="fill-height">
          <v-col
            cols="12"
            class="fill-height"
          >
            <v-card
              variant="flat"
              class="tab-content-card fill-height"
            >
              <div v-show="!ficha?.codigo && listaDisponiveisExibicao.length > 0">
                <v-text-field 
                  v-model="searchDisponiveis" 
                  density="compact"
                  variant="outlined"
                  class="search-field"
                  prepend-inner-icon="search"
                  placeholder="Buscar pacientes..."
                  hide-details
                  single-line
                  clearable
                />
                <v-list
                  class="patient-list tall-list"
                  lines="two"
                >
                  <paciente-list-item
                    v-for="(item, index) in listaDisponiveisFiltrada"
                    :key="item.idAtendimento || index"
                    :paciente="item"
                    :list-index="index + 1"
                    :is-espera="item.status === 3"
                    :is-pendente="item.status === 2 || item.status === 4"
                    class="patient-item"
                    :class="{ 'item-disabled': bloquearLista && pacienteSelecionado && pacienteSelecionado.idAtendimento !== item.idAtendimento }"
                    :is-selected="pacienteSelecionado && pacienteSelecionado.idAtendimento === item.idAtendimento"
                    :disabled="bloquearLista && pacienteSelecionado && pacienteSelecionado.idAtendimento !== item.idAtendimento"
                    @toggle="selecionarPaciente"
                    @iniciou-atendimento="onAtendimentoIniciado"
                    @observar-prontuario="onObservarProntuario"
                    @chamada-realizada="onChamadaRealizada"
                    @chamada-expirada="onChamadaExpirada"
                  />
                </v-list>
              </div>
          
              <div
                v-if="listaDisponiveisExibicao.length === 0 && !loading"
                class="empty-state"
              >
                <span class="material-icons empty-icon">group_off</span>
                <div class="empty-text">
                  Nenhum paciente disponível no momento
                </div>
              </div>
          
              <div
                v-if="loading"
                class="loading-state"
              >
                <v-skeleton-loader 
                  v-for="i in 5" 
                  :key="i" 
                  type="list-item-avatar-two-line" 
                  class="skeleton-item"
                />
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-window-item>

      <!-- Tab Espera -->
      <v-window-item value="espera">
        <v-row class="fill-height">
          <v-col
            cols="12"
            class="fill-height"
          >
            <v-card
              variant="flat"
              class="tab-content-card fill-height"
            >
              <div v-show="!ficha?.codigo && listaEspera.length > 0">
                <v-text-field 
                  v-model="searchEspera" 
                  density="compact"
                  variant="outlined"
                  class="search-field"
                  prepend-inner-icon="search"
                  placeholder="Buscar pacientes..."
                  hide-details
                  single-line
                  clearable
                />
                <v-list
                  class="patient-list tall-list"
                  lines="two"
                >
                  <paciente-list-item
                    v-for="(item, index) in listaEsperaFiltrada"
                    :key="item.idAtendimento || index"
                    :paciente="item"
                    :list-index="index + 1"
                    :is-espera="true"
                    class="patient-item"
                    :class="{ 'item-disabled': bloquearLista && pacienteSelecionado && pacienteSelecionado.idAtendimento !== item.idAtendimento }"
                    :is-selected="pacienteSelecionado && pacienteSelecionado.idAtendimento === item.idAtendimento"
                    :disabled="bloquearLista && pacienteSelecionado && pacienteSelecionado.idAtendimento !== item.idAtendimento"
                    @toggle="selecionarPaciente"
                    @iniciou-atendimento="onAtendimentoIniciado"
                    @observar-prontuario="onObservarProntuario"
                    @chamada-realizada="onChamadaRealizada"
                    @chamada-expirada="onChamadaExpirada"
                  />
                </v-list>
              </div>
          
              <div
                v-if="listaEspera.length === 0 && !loading"
                class="empty-state"
              >
                <span class="material-icons empty-icon">pause_circle</span>
                <div class="empty-text">
                  Nenhum paciente em espera
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-window-item>

      <!-- Tab Atendidos -->
      <v-window-item value="atendidos">
        <v-row class="fill-height">
          <v-col
            cols="12"
            class="fill-height"
          >
            <v-card
              variant="flat"
              class="tab-content-card fill-height"
            >
              <div v-show="!ficha?.codigo && listaAtendidos.length > 0">
                <v-text-field 
                  v-model="searchAtendidos" 
                  density="compact"
                  variant="outlined"
                  class="search-field"
                  prepend-inner-icon="search"
                  placeholder="Buscar pacientes..."
                  hide-details
                  single-line
                  clearable
                />
                <v-list
                  class="patient-list tall-list"
                  lines="two"
                >
                  <paciente-list-item
                    v-for="(item, index) in listaAtendidosFiltrada"
                    :key="item.idAtendimento || index"
                    :paciente="item"
                    :list-index="index + 1"
                    class="patient-item"
                    :class="{ 'item-disabled': bloquearLista && pacienteSelecionado && pacienteSelecionado.idAtendimento !== item.idAtendimento }"
                    :is-selected="pacienteSelecionado && pacienteSelecionado.idAtendimento === item.idAtendimento"
                    :disabled="bloquearLista && pacienteSelecionado && pacienteSelecionado.idAtendimento !== item.idAtendimento"
                    @toggle="selecionarPaciente"
                    @iniciou-atendimento="onAtendimentoIniciado"
                    @observar-prontuario="onObservarProntuario"
                    @chamada-realizada="onChamadaRealizada"
                    @chamada-expirada="onChamadaExpirada"
                  />
                </v-list>
              </div>
          
              <div
                v-if="listaAtendidos.length === 0 && !loadingAtendidos"
                class="empty-state"
              >
                <span class="material-icons empty-icon">check_circle</span>
                <div class="empty-text">
                  Nenhum paciente atendido
                </div>
              </div>

              <div
                v-if="loadingAtendidos"
                class="loading-state"
              >
                <v-skeleton-loader 
                  v-for="i in 5" 
                  :key="i" 
                  type="list-item-avatar-two-line" 
                  class="skeleton-item"
                />
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-window-item>
    </v-window>

    <!-- Floating Action Button para refresh -->
    <v-fab-transition>
      <v-btn
        color="primary"
        fab
        rounded
        class="refresh-fab"
        :loading="loading"
        @click="refreshData"
      >
        <span class="material-icons">refresh</span>
      </v-btn>
    </v-fab-transition>
    <v-divider />
  </v-card>
</template>

<script>
import medicoService from "../../services/medico.service"
import pacienteListItem from "./pacienteListItem.vue"
import { getToken, setToken } from "../../store/auth"
import io from "socket.io-client"
import socketService from "../../services/socket.service"

export default {
  name: 'PacientesListModern',

  components: {
    pacienteListItem
  },
  emits: ['selecionar-paciente', 'iniciou-atendimento', 'observar-prontuario'],
  
  data() {
    return {
      pacienteSelecionado: null,
      tab: 'disponiveis',
      loading: false,
      loadingAtendidos: false,
      searchDisponiveis: '',
      searchEspera: '',
      searchAtendidos: '',
      
      // Listas de pacientes
      listaDisponiveis: [],
      listaPendentes: [],
      listaEspera: [],
      listaAtendidos: [],
      qtdChamadasPrioridade: 0,

      // Socket
      socket: null,
      socketConnected: false,
      socketLoading: true,
      intervalos: [],

      // Estados do atendimento
      fezChamada: false,
      isAtendimento: false,
      ficha: {},
      bloquearLista: false,
      chamadasEsperaIds: [],

      // Configurações
      listaServicosEscolhidos: [],
      localEscolhido: null,
      unidadeSelecionada: null,
      nomeConsultorioCompleto: '',
      isLocalAutomatico: false
    }
  },

  computed: {
    isAutoLogin() {
      return Boolean(this.$store.getters.usuario?.autoLogin)
    },
    listaDisponiveisFiltrada() {
      return this.filtrarLista(this.listaDisponiveisExibicao, this.searchDisponiveis)
    },

    listaEsperaFiltrada() {
      return this.filtrarLista(this.listaEspera, this.searchEspera)
    },

    listaAtendidosFiltrada() {
      return this.filtrarLista(this.listaAtendidos, this.searchAtendidos)
    },

    listaDisponiveisExibicao() {
      return [...this.listaDisponiveis, ...this.listaPendentes]
    }
  },

  watch: {
    tab(newTab, oldTab) {
      if (newTab !== oldTab) {
        this.pacienteSelecionado = null;
        this.bloquearLista = false;
        this.$nextTick(() => this.garantirSelecaoAtual())
      }
    },
    listaDisponiveis() {
      this.calcularPrioridades()
      this.garantirSelecaoAtual()
    },
    listaPendentes() {
      this.calcularPrioridades()
      this.garantirSelecaoAtual()
    },
    listaEspera() {
      this.garantirSelecaoAtual()
    },
    listaAtendidos() {
      this.garantirSelecaoAtual()
    },
    pacienteSelecionado(novoPaciente) {
      if (!novoPaciente) {
        this.$nextTick(() => this.garantirSelecaoAtual())
      }
    }
  },

  async mounted() {
    await this.inicializarConfiguracoes()
    await this.inicializarSocket()
    this.retornarPacientesDisponiveis(true)
    this.retornarConsultasFinalizadas()
    
    // Refresh automático a cada 30 segundos
    this.intervalos.push(setInterval(() => {
      this.refreshToken()
    }, 30000))
  },

  beforeUnmount() {
    this.limparIntervalos()
    this.removerListenersSocket()
  },

  methods: {
    onChamadaRealizada(payload) {
      this.bloquearLista = true;
      const idAtendimento = payload?.idAtendimento;
      const status = payload?.status;
      if (idAtendimento && (payload?.origemChamada === 'espera' || status === 3)) {
        if (!this.chamadasEsperaIds.includes(idAtendimento)) {
          this.chamadasEsperaIds.push(idAtendimento)
        }
      }
    },
    onChamadaExpirada() {
      this.bloquearLista = false;
    },
    onAtendimentoIniciado(paciente) {
      this.bloquearLista = false;
      this.$emit('iniciou-atendimento', paciente);
    },
    onObservarProntuario(paciente) {
      this.$emit('observar-prontuario', this.normalizarPacienteAgrupado(paciente));
    },
    onAcaoFinalizada() {
      this.bloquearLista = false;
      this.pacienteSelecionado = null;
    },
    selecionarPaciente(paciente) {
      if (!paciente) return
      if (this.bloquearLista && this.pacienteSelecionado && this.pacienteSelecionado.idAtendimento !== paciente.idAtendimento) {
        return
      }
      this.pacienteSelecionado = paciente
    },

    obterListaPorTab(tabAtual = this.tab) {
      const listas = {
        disponiveis: this.listaDisponiveisExibicao,
        espera: this.listaEspera,
        atendidos: this.listaAtendidos
      }
      return listas[tabAtual] || []
    },

    garantirSelecaoAtual() {
      const listaAtual = this.obterListaPorTab()

      if (!listaAtual || listaAtual.length === 0) {
        if (this.pacienteSelecionado !== null) {
          this.pacienteSelecionado = null
        }
        return
      }

      if (
        this.pacienteSelecionado &&
        listaAtual.some(paciente => paciente.idAtendimento === this.pacienteSelecionado.idAtendimento)
      ) {
        return
      }
      this.pacienteSelecionado = null
    },

    getBadgeColor(tipo) {
      const count = tipo === 'disponiveis'
        ? this.listaDisponiveisExibicao.length
        : this[`lista${tipo.charAt(0).toUpperCase() + tipo.slice(1)}`].length
      
      if (count <= 0) return 'grey'
      
      const colors = {
        disponiveis: 'success',
        espera: 'yellow-darken-2',
        atendidos: 'blue'
      }
      
      return colors[tipo] || 'grey'
    },

    filtrarLista(lista, termo) {
      if (!termo) return lista
      return lista.filter(paciente => 
        paciente.nomePaciente && 
        paciente.nomePaciente.toLowerCase().includes(termo.toLowerCase())
      )
    },

    async refreshData() {
      await this.retornarPacientesDisponiveis()
      await this.retornarConsultasFinalizadas()
    },

    async inicializarConfiguracoes() {
      const usuario = this.$store.getters.usuario
      if (!usuario) return

      this.listaServicosEscolhidos = usuario.servicosEscolhidos || []
      this.localEscolhido = {
        idLocal: usuario.idLocal,
        nomeLocal: usuario.nomeLocal || ''
      }
      this.unidadeSelecionada = {
        idUnidade: usuario.idUnidade,
        nomeUnidade: usuario.nomeUnidade || 'Unidade'
      }
      this.nomeConsultorioCompleto = usuario.nomeConsultorioServicos?.nomeConsultorio || ''

      this.organizarExames()
    },

    organizarExames() {
      const listaServicosSelecionadosAux = []
      this.listaServicosEscolhidos.forEach(item => {
        listaServicosSelecionadosAux[item.idServico] = {
          ...item,
          examesIndexados: {}
        }
        
        item.exames.forEach(exame => {
          listaServicosSelecionadosAux[item.idServico].examesIndexados[exame] = exame
        })
      })
      
      this.listaServicosEscolhidos = listaServicosSelecionadosAux
    },

    async inicializarSocket() {
      this.socketLoading = true
      try {
        this.socket = io.connect(socketService.SOCKET_URL, {
          query: { token: getToken() }
        })

        this.configurarEventosSocket()
        this.escutarEventosAplicacao()

        await new Promise((resolve, reject) => {
          const timeout = setTimeout(() => reject(new Error('Timeout conexão socket')), 5000)
          
          this.socket.on('connect', () => {
            clearTimeout(timeout)
            this.socketConnected = true
            resolve()
          })
        })

      } catch (error) {
        console.error('Erro ao conectar socket:', error)
        this.socketConnected = false
        
        setTimeout(() => {
          if (!this.socketConnected) {
            this.inicializarSocket()
          }
        }, 5000)
      } finally {
        this.socketLoading = false
      }
    },

    configurarEventosSocket() {
      this.socket.on('connect', () => {
        console.log('Socket conectado')
        this.socketConnected = true
        this.mostrarNotificacao('Conectado ao servidor em tempo real', 'success')
      })

      this.socket.on('disconnect', (reason) => {
        console.log('Socket desconectado:', reason)
        this.socketConnected = false
        this.mostrarNotificacao('Conexão perdida. Tentando reconectar...', 'warning')
      })

      this.socket.on('connect_error', (error) => {
        console.error('Erro de conexão socket:', error)
        this.socketConnected = false
      })
    },

    escutarEventosAplicacao() {
      this.socket.on("atendimentoAtualizado", (mensagem) => {
        console.log("skt atendido:", mensagem)
        this.refreshData()
      })

      this.socket.on("removerPaciente", (mensagem) => {
        console.log("removerPaciente:", mensagem);
        const idMedicoAtual = this.$store.getters.usuario?.id;
        const isPacienteSelecionado = this.pacienteSelecionado && this.pacienteSelecionado.idAtendimento === mensagem.idAtendimento;

        // Se a ação foi disparada pelo médico atual (verificado pelo idMedico ou pelo paciente selecionado), não remove.
        if ((mensagem.idMedico && mensagem.idMedico == idMedicoAtual) || isPacienteSelecionado) {
            console.log(`Ação para o atendimento ${mensagem.idAtendimento} foi iniciada pelo médico atual. Ignorando remoção via 'removerPaciente'.`);
            return;
        }

        const idAtendimento = mensagem.idAtendimento;
        this.listaDisponiveis = this.listaDisponiveis.filter(p => p.idAtendimento !== idAtendimento);
        this.listaPendentes = this.listaPendentes.filter(p => p.idAtendimento !== idAtendimento);
        this.listaEspera = this.listaEspera.filter(p => p.idAtendimento !== idAtendimento);
      });

      this.socket.on("chamandoPaciente", (mensagem) => {
        console.log("skt chamandoPaciente:", mensagem)
        
        const idMedicoAtual = this.$store.getters.usuario?.id;
        console.log(`[Debug] Verificando chamada. Medico atual: ${idMedicoAtual} (tipo: ${typeof idMedicoAtual}), Medico da chamada: ${mensagem.idMedico} (tipo: ${typeof mensagem.idMedico})`);
        const isPacienteSelecionado = this.pacienteSelecionado && this.pacienteSelecionado.idAtendimento === mensagem.idAtendimento;
        
        // Se a chamada foi feita pelo médico atual (verificando pelo ID ou pelo paciente selecionado como fallback)
        if ((mensagem.idMedico && mensagem.idMedico == idMedicoAtual) || isPacienteSelecionado) {
          console.log("Médico atual chamou o paciente. A remoção de qualquer lista será ignorada para este cliente.");
          return; 
        }

        // Lógica para outros médicos: remove o paciente das listas de disponíveis e pendentes.
        this.listaDisponiveis = this.listaDisponiveis.filter(element => {
          if (element.idAtendimento == mensagem.idAtendimento) {
            if (!mensagem.automatico) {
              return false
            } else {
              if (mensagem.idLocal == this.localEscolhido.idLocal) {
                element.idLocalAutomatico = this.localEscolhido.idLocal
                return true
              } else {
                return false
              }
            }
          }
          return true
        })

        this.listaPendentes = this.listaPendentes.filter(element => 
          element.idAtendimento !== mensagem.idAtendimento
        )
      })
    },

    filtrarAtendimentosMedico(atendimentos) {
      const listaAtendimentos = Array.isArray(atendimentos) ? atendimentos : [atendimentos]

      return listaAtendimentos.filter(atendimento => {
        const isLocal = atendimento.idLocalFixo == null || 
                       atendimento.idLocalFixo == this.localEscolhido.idLocal
        
        if (isLocal) {
          if (atendimento.idMedico == null || atendimento.idMedico == this.$store.getters.usuario.id) {
            const servicoEscolhido = this.listaServicosEscolhidos[atendimento.idServico]
            if (servicoEscolhido != null) {
              if (atendimento.idExame == null) {
                return true
              }
              return servicoEscolhido.examesIndexados?.[atendimento.idExame] != null
            }
          }
        }
        return false
      })
    },

    normalizarPayloadAtendido(payload) {
      if (Array.isArray(payload)) {
        return payload.map(item => this.normalizarAtendimentoSocket(item))
      }

      if (payload?.consultas && Array.isArray(payload.consultas)) {
        const base = payload.atendimento || {}
        return payload.consultas.map(consulta => this.normalizarAtendimentoSocket({
          ...base,
          ...consulta,
          idAtendimento: consulta?.idAtendimento ?? base?.idAtendimento ?? payload?.idAtendimento,
          codigo: consulta?.codigo ?? base?.codigo,
          nomePaciente: consulta?.nomePaciente ?? base?.nomePaciente,
          numeroPaciente: consulta?.numeroPaciente ?? base?.numeroPaciente,
          dataNascimento: consulta?.dataNascimento ?? base?.dataNascimento,
          dataChegada: consulta?.dataChegada ?? base?.dataChegada,
          idPaciente: consulta?.idPaciente ?? base?.idPaciente,
          idOrdemServico: consulta?.idOrdemServico ?? base?.idOrdemServico,
          idLocal: consulta?.idLocal ?? base?.idLocal
        }))
      }

      if (payload && typeof payload === 'object') {
        return [this.normalizarAtendimentoSocket(payload)]
      }

      return []
    },

    normalizarAtendimentoSocket(atendimento) {
      const statusNormalizado = this.normalizarStatusAtendimento(atendimento?.status)
      return {
        ...atendimento,
        status: statusNormalizado
      }
    },

    normalizarStatusAtendimento(status) {
      if (status === null || status === undefined) return undefined
      if (typeof status === 'number') return status

      const texto = String(status).trim().toLowerCase()
      if (!texto) return undefined
      if (texto === 'disponivel' || texto === 'disponível') return 1
      if (texto === 'pendente') return 2
      if (texto === 'espera') return 3
      if (texto === 'atendido' || texto === 'finalizado') return 4

      const numerico = Number(texto)
      return Number.isNaN(numerico) ? undefined : numerico
    },

    removerAtendimentoDasListas(idAtendimento, listaPreservada = null) {
      if (!idAtendimento) return
      const listas = ['listaDisponiveis', 'listaPendentes', 'listaEspera']
      listas.forEach(nomeLista => {
        if (nomeLista === listaPreservada) return
        this[nomeLista] = this[nomeLista].filter(item => item.idAtendimento !== idAtendimento)
      })
    },

    upsertAtendimentoNaLista(nomeLista, atendimento, campoOrdenacao = 'dataFinalAtendimento', indiceMinimo = 0) {
      const lista = this[nomeLista]
      if (!Array.isArray(lista)) return

      const idxExistente = lista.findIndex(item => item.idAtendimento === atendimento.idAtendimento)
      if (idxExistente !== -1) {
        lista.splice(idxExistente, 1)
      }

      const valorAtual = this.obterTimestampOrdenacao(atendimento, campoOrdenacao)
      const indexOrdenado = lista.findIndex(item => this.obterTimestampOrdenacao(item, campoOrdenacao) > valorAtual)
      const indexInsercaoBase = indexOrdenado === -1 ? lista.length : indexOrdenado
      const indexInsercao = Math.max(indexInsercaoBase, indiceMinimo)

      lista.splice(indexInsercao, 0, atendimento)
    },

    obterTimestampOrdenacao(item, campoOrdenacao) {
      const candidatos = [
        item?.[campoOrdenacao],
        item?.dataChegada,
        item?.dataStatus,
        item?.dataFinalAtendimento
      ]

      for (const valor of candidatos) {
        if (!valor) continue
        const timestamp = new Date(valor).getTime()
        if (!Number.isNaN(timestamp)) return timestamp
      }

      return 0
    },

    formatarAtendimentosTabela(atendimento) {
      const atendimentoAux = { ...atendimento[0], atendimentos: [] }
      
      for (let i = 1; i < atendimento.length; i++) {
        atendimentoAux.atendimentos.push(atendimento[i])
      }
      
      return atendimentoAux
    },

    removerListenersSocket() {
      if (this.socket) {
        this.socket.off("atendido")
        this.socket.off("removerPaciente")
        this.socket.off("chamandoPaciente")
        this.socket.off("connect")
        this.socket.off("disconnect")
        this.socket.off("connect_error")
        this.socket.disconnect()
      }
    },

    async retornarPacientesDisponiveis(primeiroAcesso = false) {
      this.loading = true
      try {
        const usuario = this.$store.getters.usuario
        console.log('usuario: ', usuario);
        
        if (!usuario || !this.listaServicosEscolhidos || this.listaServicosEscolhidos.length === 0) {
          return
        }
        // (listaServicos, primeiroAcesso, nomeLocal, tecnico, idMedico = null, idUnidade = null)
        const response = await medicoService.medico.retornarPacientesDisponiveis(
          Object.values(this.listaServicosEscolhidos),
          primeiroAcesso,
          this.localEscolhido.nomeLocal,
          usuario.tecnico || 0,
          usuario.id,
          usuario.idUnidade
        )
        
        if (response.data && Array.isArray(response.data)) {
          this.organizarListasPacientes(response.data)
        }
      } catch (error) {
        console.error('Erro ao carregar pacientes disponíveis:', error)
      } finally {
        this.loading = false
      }
    },

    async retornarConsultasFinalizadas() {
      this.loadingAtendidos = true
      try {
        const usuario = this.$store.getters.usuario
        if (!usuario) return

        const response = await medicoService.medico.retornarConsultasFinalizadas(
          usuario.id, 
          usuario.idUnidade
        )
        
        if (response.data) {
          response.data.forEach(item => { item.status = 4 })
          this.listaAtendidos = response.data
          this.organizarListasPacientesAtendidos()
        }
      } catch (error) {
        console.error('Erro ao carregar consultas finalizadas:', error)
      } finally {
        this.loadingAtendidos = false
      }
    },

    organizarListasPacientes(pacientes) {
      if (!pacientes || pacientes.length === 0) return
      
      const listaAgrupada = []
      let elementoPrincipal = { ...pacientes[0], atendimentos: [] }
      listaAgrupada.push(elementoPrincipal)

      for (let i = 1; i < pacientes.length; i++) {
        const elemento = pacientes[i]
        if (elemento.idAtendimento !== elementoPrincipal.idAtendimento) {
          elementoPrincipal = { ...elemento, atendimentos: [] }
          listaAgrupada.push(elementoPrincipal)
        } else {
          elementoPrincipal.atendimentos.push(elemento)
        }
      }

      this.listaDisponiveis = []
      this.listaPendentes = []
      this.listaEspera = []

      listaAgrupada.forEach(paciente => {
        const foiChamadaDeEspera = this.chamadasEsperaIds.includes(paciente.idAtendimento)

        if ((paciente.status === 2 || paciente.status === 4) && foiChamadaDeEspera) {
          const serverDate = new Date(paciente.dataStatus)
          paciente.dataStatusCronometro = serverDate.getTime()
          paciente.status = 3
          this.listaEspera.push(paciente)
          return
        }

        if (paciente.status === 1) {
          this.listaDisponiveis.push(paciente)
          paciente.jaChamado = false
        } else if (paciente.status === 2 || paciente.status === 4) {
          this.listaPendentes.push(paciente)
        } else if (paciente.status === 3) {
          const serverDate = new Date(paciente.dataStatus)
          paciente.dataStatusCronometro = serverDate.getTime()
          this.listaEspera.push(paciente)
        }
      })

      this.calcularPrioridades()
    },

    organizarListasPacientesAtendidos() {
      const origem = this.listaAtendidos || []
      if (origem.length === 0) return

      const agrupado = new Map()
      origem.forEach((elemento, index) => {
        const id = elemento.idAtendimento ?? elemento.idConsulta ?? elemento.id ?? `idx_${index}`
        if (!agrupado.has(id)) {
          agrupado.set(id, { ...elemento, atendimentos: [] })
        } else {
          agrupado.get(id).atendimentos.push(elemento)
        }
      })

      const listaAgrupada = Array.from(agrupado.values())
        .map((paciente) => this.normalizarPacienteAgrupado(paciente))
      listaAgrupada.sort((a, b) => {
        const aKey = this.obterChaveOrdenacaoAtendido(a)
        const bKey = this.obterChaveOrdenacaoAtendido(b)
        if (aKey !== bKey) return bKey - aKey
        return (b.idAtendimento || 0) - (a.idAtendimento || 0)
      })

      this.listaAtendidos = listaAgrupada
    },

    obterChaveOrdenacaoAtendido(paciente) {
      const data = paciente?.dataFinalAtendimento || paciente?.dataStatus || paciente?.dataAtendimento
      if (!data) return 0
      const time = new Date(data).getTime()
      return Number.isNaN(time) ? 0 : time
    },

    normalizarPacienteAgrupado(paciente) {
      if (!paciente) return paciente

      const relacionados = [paciente, ...(paciente.atendimentos || [])]
      const pick = (...keys) => {
        for (const item of relacionados) {
          for (const key of keys) {
            if (item?.[key] !== undefined && item?.[key] !== null && item?.[key] !== '') {
              return item[key]
            }
          }
        }
        return null
      }

      return {
        ...paciente,
        idExamesos: pick('idExamesos', 'idExamesOS'),
        idExamesOS: pick('idExamesOS', 'idExamesos'),
        idConsulta: pick('idConsulta'),
        idOrdemServico: pick('idOrdemServico'),
        idProntuario: pick('idProntuario'),
        idServico: pick('idServico')
      }
    },

    prioridadeValor(paciente) {
      const direto = paciente?.prioridade
      if (direto !== null && direto !== undefined && direto !== '') {
        const num = Number(direto)
        return Number.isNaN(num) ? null : num
      }
      const atendimentos = paciente?.atendimentos || []
      for (const atendimento of atendimentos) {
        const valor = atendimento?.prioridade
        if (valor !== null && valor !== undefined && valor !== '') {
          const num = Number(valor)
          return Number.isNaN(num) ? null : num
        }
      }
      return null
    },

    isPrioritario(paciente) {
      const valor = this.prioridadeValor(paciente)
      return valor === 1 || valor === 3
    },

    calcularPrioridades() {
      this.qtdChamadasPrioridade = 0
      this.listaDisponiveis.forEach(paciente => {
        if (this.isPrioritario(paciente)) {
          this.qtdChamadasPrioridade += 1
        }
      })
    },

    limparIntervalos() {
      this.intervalos.forEach(clearInterval)
      this.intervalos = []
    },

    refreshToken() {
      const usuario = this.$store.getters.usuario
      if (!usuario) return

      medicoService.medico.refreshToken(
        getToken(),
        usuario.idLocal,
        usuario.idUnidade
      ).then(rsp => {
        if (rsp.status == 200) {
          setToken(rsp.data.token)
        }
      }).catch(err => {
        console.error('Erro ao refresh token:', err)
      })
    },

    mostrarNotificacao(mensagem, tipo = 'info') {
      console.log(`${tipo}: ${mensagem}`)
    }
  }
}
</script>

<style scoped>
.modern-card {
  border-radius: 0px;
  overflow: hidden;
  position: relative;
  background: rgb(var(--v-theme-surface));
  height: 100%; /* Alterado de 85% para 100% para ocupar toda a altura disponível */
  /* min-height: 700px; */ /* Removido para permitir que o componente seja mais flexível em telas menores */
  flex-direction: column;
}

.connection-status {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 16px;
  font-size: 12px;
  font-weight: 500;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  flex-shrink: 0;
}

.connection-status.connected {
  background: linear-gradient(90deg, rgba(var(--v-theme-success), 0.1), rgba(var(--v-theme-success), 0.05));
  color: rgb(var(--v-theme-success));
}

.connection-status.disconnected {
  background: linear-gradient(90deg, rgba(var(--v-theme-error), 0.1), rgba(var(--v-theme-error), 0.05));
  color: rgb(var(--v-theme-error));
}

.icon-sm {
  font-size: 16px;
  margin-right: 6px;
}

/* Tabs mais largas e melhoradas */
.modern-tabs.wide-tabs {
  background: linear-gradient(135deg, rgb(var(--v-theme-primary)) 0%, rgb(var(--v-theme-secondary)) 100%);
  min-height: 90px;
  flex-shrink: 0;
}

.modern-tabs.wide-tabs :deep(.v-tab) {
  color: rgb(var(--v-theme-on-primary)) !important;
  opacity: 0.8;
  transition: all 0.3s ease;
  min-width: 180px !important;
  max-width: 220px !important;
  flex: 1 1 auto;
}

.modern-tabs.wide-tabs :deep(.v-tab--selected) {
  opacity: 1;
  background: rgba(var(--v-theme-on-primary), 0.15);
  backdrop-filter: blur(10px);
  transform: translateY(-2px);
}

.wide-tab {
  padding: 16px 20px !important;
  position: relative;
  min-height: 90px;
  border-radius: 0;
}

.wide-tab-content {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  justify-content: flex-start;
}

.tab-text-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  flex: 1;
}

.icon-badge-container {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.tab-icon {
  font-size: 28px !important;
  opacity: 0.9;
}

.icon-badge {
  position: absolute;
  top: -6px;
  right: -6px;
}

.icon-badge :deep(.v-badge__badge) {
  font-size: 11px;
  font-weight: 700;
  min-width: 20px;
  height: 20px;
  border: 2px solid rgb(var(--v-theme-surface));
  background: rgb(var(--v-theme-error));
}

.tab-label {
  font-size: 14px !important;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  text-align: left;
  color: rgb(var(--v-theme-on-primary));
}

.priority-indicator {
  background: #4dabf7;
  color: #ffffff;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  animation: pulse 2s infinite;
  white-space: nowrap;
}

@keyframes pulse {
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
}

.modern-tab.has-priority {
  animation: gentle-pulse 3s infinite;
}

@keyframes gentle-pulse {
  0% { background: rgba(77, 171, 247, 0.1); }
  50% { background: rgba(77, 171, 247, 0.2); }
  100% { background: rgba(77, 171, 247, 0.1); }
}

/* Área principal com altura aumentada */
.modern-window {
  background: rgb(var(--v-theme-background));
  flex: 1;
  min-height: 630px;
  display: flex;
  flex-direction: column;
}

.tab-content-card {
  background: transparent;
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.search-field {
  margin: 12px 16px 8px 16px;
  border-radius: 12px;
  flex-shrink: 0;
}

.search-field :deep(.v-input__slot) {
  border-radius: 12px !important;
  background: rgb(var(--v-theme-surface));
}

/* Lista de pacientes com altura aumentada */
.patient-list {
  background: transparent;
  padding: 0;
  flex: 1;
  overflow-y: auto;
}

.patient-list.tall-list {
  max-height: calc(100vh - 220px) !important;
  min-height: calc(100vh - 260px);
  overflow-x: hidden;
}

.patient-item {
  margin: 0 22px 4px 22px;
  border-radius: 12px;
  transition: all 0.3s ease;
  background: rgb(var(--v-theme-surface));
}

.patient-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.paciente-selecionado {
  border: 2px solid rgb(var(--v-theme-primary));
  box-shadow: 0 4px 15px rgba(var(--v-theme-primary), 0.3);
}

.patient-item.item-disabled {
  pointer-events: none;
  opacity: 0.5;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
  flex: 1;
}

.empty-icon {
  font-size: 64px;
  margin-bottom: 16px;
  opacity: 0.4;
  color: rgb(var(--v-theme-disabled));
}

.empty-text {
  color: rgba(var(--v-theme-on-surface), 0.6);
  font-size: 14px;
  font-weight: 500;
}

.loading-state {
  padding: 8px;
  flex: 1;
}

.skeleton-item {
  margin: 8px;
  border-radius: 12px;
}

.refresh-fab {
  position: absolute;
  bottom: 16px;
  right: 16px;
  z-index: 10;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  background: rgb(var(--v-theme-primary)) !important;
  color: rgb(var(--v-theme-on-primary)) !important;
}

.refresh-fab:hover {
  background: rgb(var(--v-theme-secondary)) !important;
}

/* Scrollbar personalizado */
.patient-list::-webkit-scrollbar {
  width: 8px;
}

.patient-list::-webkit-scrollbar-track {
  background: rgb(var(--v-theme-surface-variant));
  border-radius: 4px;
}

.patient-list::-webkit-scrollbar-thumb {
  background: rgb(var(--v-theme-primary), 0.5);
  border-radius: 4px;
}

.patient-list::-webkit-scrollbar-thumb:hover {
  background: rgb(var(--v-theme-primary), 0.7);
}

/* Responsividade para as tabs largas */
@media (max-width: 1200px) {
  .modern-tabs.wide-tabs :deep(.v-tab) {
    min-width: 160px !important;
    max-width: 180px !important;
  }
  
  .wide-tab {
    padding: 14px 16px !important;
  }
  
  .tab-icon {
    font-size: 24px !important;
  }
  
  .tab-label {
    font-size: 13px !important;
  }
  
  .patient-list.tall-list {
    max-height: 450px !important;
    min-height: 400px;
  }
}

@media (max-width: 960px) {
  .modern-tabs.wide-tabs :deep(.v-tab) {
    min-width: 140px !important;
    max-width: 160px !important;
  }
  
  .wide-tab-content {
    gap: 8px;
  }
  
  .tab-icon {
    font-size: 22px !important;
  }
  
  .tab-label {
    font-size: 12px !important;
  }
  
  .priority-indicator {
    font-size: 10px;
    padding: 2px 8px;
  }
  
  .patient-list.tall-list {
    max-height: 400px !important;
    min-height: 350px;
  }
}

@media (max-width: 768px) {
  .modern-tabs.wide-tabs {
    min-height: 80px;
  }
  
  .modern-tabs.wide-tabs :deep(.v-tab) {
    min-width: 120px !important;
    max-width: 140px !important;
  }
  
  .wide-tab {
    padding: 12px 8px !important;
    min-height: 80px;
  }
  
  .wide-tab-content {
    flex-direction: column;
    gap: 4px;
  }
  
  .tab-text-content {
    align-items: center;
  }
  
  .tab-label {
    text-align: center;
    font-size: 11px !important;
  }
  
  .tab-icon {
    font-size: 20px !important;
  }
  
  .icon-badge :deep(.v-badge__badge) {
    font-size: 9px;
    min-width: 16px;
    height: 16px;
  }
  
  .patient-list.tall-list {
    max-height: 350px !important;
    min-height: 300px;
  }
  
  .modern-card {
    min-height: 600px;
  }
}

.material-icons {
  font-family: 'Material Icons';
  font-weight: normal;
  font-style: normal;
  line-height: 1;
  letter-spacing: normal;
  text-transform: none;
  display: inline-block;
  white-space: nowrap;
  word-wrap: normal;
  direction: ltr;
  -webkit-font-feature-settings: 'liga';
  -webkit-font-smoothing: antialiased;
}

/* Garantir que o layout flex funcione corretamente */
:deep(.v-window__container) {
  height: 100%;
  flex: 1;
}

:deep(.v-window-item) {
  height: 100%;
}
</style>
